import React, { useEffect, useState, useRef } from "react"
import { Box, Image, AspectRatio, Flex, useBreakpointValue, Button } from "@chakra-ui/react"
import { Swiper, SwiperSlide } from "swiper/react"
import { Navigation, Pagination } from "swiper"

import ProductBadge from "@app/components/Product/ProductBadge"
import Icon from "@app/components/Icon"
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch"

import "swiper/css/navigation"
import "swiper/css/pagination"

type Props = {
  images: any
  badges: Array<GatsbyTypes.SanitySettingProductBadges>
}

const ProductMedia: React.FC<Props> = ({ images, badges }) => {
  const slidesPerView = useBreakpointValue({ base: 1, lg: 1.25 }) || 1
  const isMobile = useBreakpointValue({ base: true, md: false })
  const [draggable, setDraggable] = useState(true)
  const [imageZoom, setImageZoom] = useState<null | string>(null)
  const [isZoomed, setIsZoomed] = useState(false)
  const swiperRef = useRef(null)

  useEffect(() => {
    if (swiperRef.current && swiperRef.current.swiper) {
      swiperRef.current.swiper.allowTouchMove = draggable
    }
  }, [draggable])

  const handleZoom = (e: MouseEvent, media?: string): void => {
    setDraggable(false)

    if (isMobile) document.body.style.overflow = "hidden"
    if (!imageZoom) setImageZoom(media || "")
    const targetElement = e.currentTarget as HTMLDivElement
    const imgs = targetElement.querySelectorAll("img")
    const img = imgs[imgs.length - 1]
    if (!img) return

    const boundingRect = targetElement.getBoundingClientRect()
    const x = e.clientX - boundingRect.left
    const y = e.clientY - boundingRect.top
    img.style.transformOrigin = `${x}px ${y}px`
    img.style.transform = "scale(3)"
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onPinchZoomStop = (ref: any, event: any) => {
    const scale = ref.state.scale
    scale > 1 ? setDraggable(false) : setDraggable(true)
    scale > 1 ? setIsZoomed(true) : setIsZoomed(false)
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onPinchZoomStart = (ref: any, event: any) => {
    const scale = ref.state.scale
    scale > 1 ? setDraggable(false) : setDraggable(true)
    scale > 1 ? setIsZoomed(true) : setIsZoomed(false)
  }

  const offZoom = (e: MouseEvent) => {
    setDraggable(true)
    document.body.style.overflow = "auto"
    const targetElement = e.currentTarget as HTMLDivElement
    const imgs = targetElement.querySelectorAll("img")
    const img = imgs[imgs.length - 1]
    if (!img) return
    img.style.transformOrigin = `center bottom`
    img.style.transform = "scale(1)"
    setImageZoom(null)
  }

  return images.length ? (
    <>
      <Flex width="100%">
        <Box
          flex="1"
          background="white"
          maxWidth="100%"
          position="relative"
          sx={{
            ".swiper": {
              allowTouchMove: "false",
            },
            ".swiper-wrapper, .swiper-slide": {
              allowTouchMove: "false!important",
            },
            ".swiper-pagination-progressbar": {
              display: "inline-block",
              position: "absolute",
              top: "95%",
              left: { base: "0%", lg: "5%" },
              height: "3px",
              background: "primary.greyLight",
              width: { base: "100%", lg: "53%" },
              zIndex: "99",
              pointerEvents: "none",
              allowTouchMove: "false",
            },
            ".swiper-pagination-progressbar-fill": {
              background: "primary.navy",
            },
            ".swiper-button-next, .swiper-button-prev": {
              w: "48px",
              h: "48px",
              border: "1px solid",
              borderColor: "primary.greyLight",
              right: "unset",
              left: "unset",
            },
            ".swiper-disabled": {
              pointerEvents: "unset!important",
            },
          }}
        >
          <Swiper
            ref={swiperRef}
            slidesPerView={slidesPerView}
            modules={[Navigation, Pagination]}
            pagination={{ type: "progressbar" }}
            navigation={{
              nextEl: ".swiper-button-next",
              prevEl: ".swiper-button-prev",
              disabledClass: "swiper-disabled",
            }}
            allowTouchMove={draggable}
          >
            {images.map((image: any, index: number) => (
              <SwiperSlide
                key={index}
                onClick={e => (isMobile ? null : imageZoom ? offZoom(e) : handleZoom(e, image))}
                onMouseLeave={e => offZoom(e)}
                onMouseMove={e => image === imageZoom && handleZoom(e)}
              >
                <>
                  {index === 0 && badges.length > 0 && <ProductBadge badges={badges} pdp />}
                  <AspectRatio
                    ratio={4 / 5}
                    sx={{
                      ".react-transform-component": {
                        w: "100%",
                        h: "100%",
                      },
                    }}
                  >
                    {isMobile ? (
                      <TransformWrapper
                        initialScale={1}
                        minScale={1}
                        initialPositionX={0}
                        initialPositionY={0}
                        panning={{ disabled: !isZoomed }}
                        onPinchingStart={(ref, event) => onPinchZoomStart(ref, event)}
                        onPinchingStop={(ref, event) => onPinchZoomStop(ref, event)}
                      >
                        <TransformComponent>
                          <Image
                            w="100%"
                            h="100%"
                            src={image?.src}
                            srcSet={image?.srcSet}
                            objectFit="cover !important"
                            loading={index <= 1 ? "eager" : "lazy"}
                            cursor={imageZoom ? "zoom-out" : "zoom-in"}
                          />
                        </TransformComponent>
                      </TransformWrapper>
                    ) : (
                      <Image
                        w="100%"
                        h="100%"
                        src={image?.src}
                        srcSet={image?.srcSet}
                        objectFit="cover !important"
                        loading={index <= 1 ? "eager" : "lazy"}
                        cursor={imageZoom ? "zoom-out" : "zoom-in"}
                      />
                    )}
                  </AspectRatio>
                </>
              </SwiperSlide>
            ))}
          </Swiper>
          <Flex gap={"12px"} position="absolute" top="95%" right="22%" display={{ base: "none", lg: "flex" }}>
            <Button className="swiper-button-prev" _after={{ content: '""', fontSize: "0 !important" }} onClick={e => offZoom(e)}>
              <Icon name="carouselChevron" height="20px" width="20px" fill="none" transform="rotate(180deg)" />
            </Button>
            <Button className="swiper-button-next" _after={{ content: '""', fontSize: "0 !important" }} onClick={e => offZoom(e)}>
              <Icon name="carouselChevron" height="20px" width="20px" fill="none" />
            </Button>
          </Flex>
        </Box>
      </Flex>
    </>
  ) : null
}

export default React.memo(ProductMedia)
